/**
 * Admin sections grid: cards for navigating to admin sections.
 * Shows available sections based on user permissions.
 * Includes "coming soon" badges for future features.
 */

import { Link } from "react-router-dom";
import {
  IconDocument,
  IconHomeHeart,
  IconUsers,
  IconCalendar,
  IconMegaphone,
  IconNewspaper,
  IconChartBar,
  IconChevronRight,
  IconCog,
  IconTag,
  IconGift,
  IconTicket,
  IconShield,
  IconHeartHands,
  IconAcademicCap,
} from "../../../components/Icons.tsx";
import { useAuth } from "../../../lib/auth.tsx";
import {
  hasOperatorAccess,
  hasAdminAccess,
  hasRaffleAccess,
  hasTicketAccess,
  hasGenderPartyAccess,
  hasAcademyAccess,
} from "../../../lib/admin-permissions.ts";

type IconComponent = React.ComponentType<{ className?: string }>;

interface AdminSection {
  id: string;
  path: string;
  label: string;
  description: string;
  icon: IconComponent;
  /** 
   * Required permission level:
   * - "operator" = payments.verify OR admin.full
   * - "admin" = admin.full only
   * - "raffle" = raffle.manage OR admin.full
   * - "tickets" = tickets.scan OR tickets.issue OR admin.full
   * - "genderParty" = gender_party.manage OR admin.full
   * - "academy" = academy.manage OR admin.full
   */
  permission: "operator" | "admin" | "raffle" | "tickets" | "genderParty" | "academy";
  /** If true, shows "Скоро" badge and disables navigation */
  comingSoon?: boolean;
}

const ADMIN_SECTIONS: AdminSection[] = [
  {
    id: "orders",
    path: "/admin/orders",
    label: "Заказы и платежи",
    description: "Платежи, верификация",
    icon: IconDocument,
    permission: "operator",
  },
  {
    id: "tickets",
    path: "/admin/tickets",
    label: "Билеты",
    description: "Сканирование и выдача",
    icon: IconTicket,
    permission: "tickets",
  },
  {
    id: "raffles",
    path: "/admin/raffles",
    label: "Розыгрыши",
    description: "Управление розыгрышами призов",
    icon: IconGift,
    permission: "raffle",
  },
  {
    id: "gender-party",
    path: "/admin/gender-party",
    label: "Gender Party",
    description: "Голосование пол ребёнка",
    icon: IconHeartHands,
    permission: "genderParty",
  },
  {
    id: "academy",
    path: "/admin/academy",
    label: "Библейская академия",
    description: "Курсы и контрольные вопросы",
    icon: IconAcademicCap,
    permission: "academy",
  },
  {
    id: "events",
    path: "/admin/events",
    label: "Мероприятия",
    description: "Создание и редактирование",
    icon: IconCalendar,
    permission: "admin",
  },
  {
    id: "categories",
    path: "/admin/categories",
    label: "Категории",
    description: "Категории событий",
    icon: IconTag,
    permission: "admin",
  },
  {
    id: "home-groups",
    path: "/admin/home-groups",
    label: "Домашние группы",
    description: "Группы, лидеры, участники",
    icon: IconHomeHeart,
    permission: "admin",
  },
  {
    id: "ministries",
    path: "/admin/ministries",
    label: "Служения",
    description: "Команды и служители",
    icon: IconUsers,
    permission: "admin",
  },
  {
    id: "broadcasts",
    path: "/admin/broadcasts",
    label: "Рассылки",
    description: "Telegram, уведомления",
    icon: IconMegaphone,
    permission: "admin",
    comingSoon: true,
  },
  {
    id: "content",
    path: "/admin/content",
    label: "Контент",
    description: "Истории, новости, объявления",
    icon: IconNewspaper,
    permission: "admin",
  },
  {
    id: "analytics",
    path: "/admin/analytics",
    label: "Аналитика",
    description: "Статистика, отчёты",
    icon: IconChartBar,
    permission: "admin",
    comingSoon: true,
  },
  {
    id: "access",
    path: "/admin/access",
    label: "Доступы",
    description: "Роли и права пользователей",
    icon: IconShield,
    permission: "admin",
  },
  {
    id: "settings",
    path: "/admin/settings",
    label: "Настройки",
    description: "Конфигурация системы",
    icon: IconCog,
    permission: "admin",
    comingSoon: true,
  },
];

function canAccessSection(
  section: AdminSection,
  permissions: string[] | undefined
): boolean {
  switch (section.permission) {
    case "operator":
      return hasOperatorAccess(permissions);
    case "raffle":
      return hasRaffleAccess(permissions);
    case "tickets":
      return hasTicketAccess(permissions);
    case "genderParty":
      return hasGenderPartyAccess(permissions);
    case "academy":
      return hasAcademyAccess(permissions);
    case "admin":
      return hasAdminAccess(permissions);
  }
}

interface SectionCardProps {
  section: AdminSection;
}

function SectionCard({ section }: SectionCardProps) {
  const Icon = section.icon;
  const isDisabled = section.comingSoon;

  const content = (
    <div
      className={`flex items-center gap-3 p-4 rounded-2xl bg-white/95 shadow-sm transition-colors ${
        isDisabled ? "opacity-60" : "active:bg-slate-50"
      }`}
    >
      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-[15px] text-slate-800 truncate">
            {section.label}
          </span>
          {section.comingSoon && (
            <span className="px-2 py-0.5 text-[10px] font-semibold text-slate-500 bg-slate-100 rounded-full shrink-0">
              Скоро
            </span>
          )}
        </div>
        <span className="text-[13px] text-slate-500 block truncate">
          {section.description}
        </span>
      </div>
      {!isDisabled && (
        <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
      )}
    </div>
  );

  if (isDisabled) {
    return <div className="cursor-not-allowed">{content}</div>;
  }

  return <Link to={section.path}>{content}</Link>;
}

export function AdminSectionsGrid() {
  const { account } = useAuth();
  const permissions = account?.permissions;

  const visibleSections = ADMIN_SECTIONS.filter((section) =>
    canAccessSection(section, permissions)
  );

  if (visibleSections.length === 0) {
    return null;
  }

  return (
    <div>
      <p className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide mb-3">
        Разделы
      </p>
      <div className="space-y-2">
        {visibleSections.map((section) => (
          <SectionCard key={section.id} section={section} />
        ))}
      </div>
    </div>
  );
}
